import * as _ from "lodash-es";
import VueScrollTo from "vue-scrollto";

/**
 * Check errors and return state value for vue-bootstrap form group.
 * @param {String[]} errors A list of errors.
 * @returns {False|undefined}
 */
export const stateBuilderCallback = (errors) => !(errors && errors.length) && undefined;

/**
 * Check errors and return invalid feedback for vue-bootstrap form group.
 * @param {String[]} errors A list of errors.
 * @returns {String|undefined}
 */
export const invalidFeedbackBuilderCallback = (errors) =>
  (errors && errors.length && (errors.join ? errors.join(" ") : errors)) || undefined;

export const handleSubmitError = (axiosError, isScrollEnabled = true) => {
  if (axiosError.response && axiosError.response.status === 400) {
    if (isScrollEnabled) {
      setTimeout(() => {
        VueScrollTo.scrollTo(".is-invalid");
      });
    }
    return axiosError.response.data;
  }
  return {};
};

export const traverseErrors = (data, errors, fn) => {
  const result = {};
  if (!errors) {
    return result;
  }
  Object.keys(data).forEach((key) => {
    if (_.isPlainObject(data[key])) {
      if (Array.isArray(errors[key])) {
        // If send an object and receive list of errors.
        result[key] = fn(errors[key]);
      } else {
        result[key] = traverseErrors(data[key], errors[key] || {}, fn);
      }
    } else if (Array.isArray(data[key])) {
      if (Array.isArray(errors[key]) && _.isString(errors[key][0])) {
        // Handle required field if empty array sent.
        result[key] = fn(errors[key]);
      } else {
        result[key] = data[key].map((d, i) => {
          let error;
          try {
            error = errors[key][i] || {};
          } catch (e) {
            error = {};
          }
          return traverseErrors(d, error, fn);
        });
      }
    } else {
      const fnRes = fn(errors[key], key);
      if (typeof fnRes !== "undefined") {
        result[key] = fnRes;
      }
    }
  });
  return result;
};

/**
 * Walk the errors and collect non field errors.
 * @param {any} errors
 * @return {Array}
 */
export const collectNonFieldErrors = (errors) => {
  if (_.isObject(errors)) {
    let nonFieldErrors = Object.values(errors).flatMap(collectNonFieldErrors);
    if (errors.non_field_errors) {
      nonFieldErrors = [...errors.non_field_errors, ...nonFieldErrors];
    }
    return nonFieldErrors;
  } else if (Array.isArray(errors)) {
    return errors.flatMap(collectNonFieldErrors);
  }
  return [];
};

/**
 * Some fields contain array of strings (or whatever) and not support
 * individual item's errors. Backend return error only for array entirely.
 *
 * @param {false|Array} state
 * @returns {false|undefined}
 */
export const getArrayFieldState = (state) => {
  if (Array.isArray(state)) {
    if (state.filter((obj) => !_.isEmpty(obj)).length !== 0) {
      // traverseErrors add empty object for initial values
      return false;
    }
    return undefined;
  }
  return state;
};

/**
 * Same as `getArrayFieldState` but for invalid-feedback.
 *
 * @param {string|Array} invalid
 * @returns {string|undefined}
 */
export const getArrayFieldInvalid = (invalid) => {
  if (Array.isArray(invalid)) {
    return invalid
      .map((obj) => obj[0])
      .filter((feedback) => feedback)
      .join(" ");
  }
  return invalid;
};

/**
 * Value is actually an object of {[key: string]: boolean}
 * so traverseErrors thinks that validation required for items of object.
 * But in fact we can get errors only for whole object.
 *
 * @param {false|Object} state
 * @returns {false|undefined}
 */
export const getObjectFieldState = (state) => {
  if (!state) {
    return state;
  }
  return undefined;
};

/**
 * Same as `getObjectFieldState` but for invalid-feedback.
 *
 * @param {string|Object} invalid
 * @returns {string|undefined}
 */
export const getObjectFieldInvalid = (invalid) => {
  if (_.isString(invalid)) {
    return invalid;
  }
  return undefined;
};

/**
 * Make options for input.
 *
 * @param {Array} options Initial options.
 * @param {null|""} emptyValue Value that considered as empty.
 * @returns {Array}
 */
export const makeOptions = (options, emptyValue = null) => {
  return [{ text: "---Please Select---", value: emptyValue }].concat(options);
};

/**
 * @param {String} label
 * @param {Boolean} required
 * @returns {String}
 */
export const buildLabel = (label, required) => label + (required ? "*:" : ":");

/**
 * If value or element of value is equal to 'other'.
 * @param {String|Array} value
 * @returns {Boolean}
 */
export const isOther = (value) => {
  if (_.isString(value)) {
    return value.toLowerCase().trim() === "other";
  }
  if (Array.isArray(value)) {
    return value.some((opt) => _.isString(opt) && opt.toLowerCase().trim() === "other");
  }
  return false;
};

/**
 *  Prepare value to display from pair of main field and _other field.
 *
 * @param {Object} data
 * @param {String} fieldName
 * @returns {String} value to display
 */
export const getOptionsFieldDisplay = (data, fieldName) => {
  const otherValue = data[fieldName + "_other"];
  if (isOther(data[fieldName]) && otherValue) {
    return otherValue;
  }
  return data[fieldName];
};

export const makeId = (prefix, name) => {
  if (prefix) {
    return `${prefix}-id_${name}`;
  }
  return `id_${name}`;
};

/**
 * Process pasted text via transformer function.
 *
 * @param {ClipboardEvent} event
 * @param {HTMLInputElement} inputElement
 * @param {Function} pasteTransformer
 */
export const handlePaste = (event, inputElement, pasteTransformer) => {
  event.preventDefault();
  event.stopPropagation();
  const text = (event.clipboardData || window.clipboardData).getData("text");
  const transformedText = pasteTransformer(text);
  const position = inputElement.selectionStart;
  const currentValue = inputElement.value;
  inputElement.value = [
    currentValue.slice(0, position),
    transformedText,
    currentValue.slice(position),
  ].join("");
  // Set cursor position
  const newPosition = position + transformedText.length;
  inputElement.setSelectionRange(newPosition, newPosition);
};
